import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import useSWR, { useSWRConfig } from "swr";
import * as authApi from "@/api/auth";
import { onMembershipLost, onUnauthorized } from "@/lib/api-client";
import { deleteLocalData } from "@/sync/local-db";
import type { PublicUser, Session, WorkspaceSummary } from "@/types/auth";
import { getSession, setSession } from "./session";

type AuthContextValue = {
	user: PublicUser | null;
	workspaces: WorkspaceSummary[];
	workspace: WorkspaceSummary | null;
	isAuthenticated: boolean;
	login: (input: { email: string; password: string }) => Promise<void>;
	signup: (input: { name: string; email: string; password: string }) => Promise<void>;
	logout: () => Promise<void>;
	switchWorkspace: (workspaceId: string) => void;
	/** Replaces the workspace list (after joining one) and makes `workspaceId` active. */
	adoptWorkspaces: (workspaces: WorkspaceSummary[], workspaceId: string) => void;
	refresh: () => Promise<unknown>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const toSession = (res: authApi.AuthResponse, preferred?: string): Session => ({
	token: res.token,
	user: res.user,
	workspaces: res.workspaces,
	workspaceId: res.workspaces.find((w) => w.id === preferred)?.id ?? res.workspaces[0]?.id ?? "",
});

/**
 * Render first, authenticate second: the session is read synchronously from localStorage so
 * the app paints from IndexedDB immediately; `/auth/me` revalidates in the background and any
 * 401 anywhere logs out.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [session, setState] = useState<Session | null>(getSession);

	const commit = useCallback((next: Session | null) => {
		setSession(next);
		setState(next);
	}, []);

	const logout = useCallback(async () => {
		const current = getSession();
		commit(null);
		if (current) await deleteLocalData(current.user.id).catch(() => {});
	}, [commit]);

	useEffect(
		() =>
			onUnauthorized(() => {
				if (!getSession()) return;
				commit(null);
				toast("Session expired", { description: "Please log in again." });
			}),
		[commit],
	);

	const { mutate } = useSWRConfig();
	const refresh = useCallback(() => mutate("/auth/me"), [mutate]);

	useEffect(
		() =>
			onMembershipLost(() => {
				const current = getSession();
				const name = current?.workspaces.find((w) => w.id === current.workspaceId)?.name;
				toast("Workspace access removed", {
					description: name ? `You're no longer a member of ${name}.` : undefined,
				});
				void refresh();
			}),
		[refresh],
	);

	useSWR(session ? "/auth/me" : null, authApi.getMe, {
		revalidateOnFocus: false,
		onSuccess: ({ user, workspaces }) => {
			const current = getSession();
			if (!current) return;
			const workspaceId = workspaces.some((w) => w.id === current.workspaceId)
				? current.workspaceId
				: (workspaces[0]?.id ?? "");
			commit({ ...current, user, workspaces, workspaceId });
		},
	});

	const value = useMemo<AuthContextValue>(() => {
		const workspace = session?.workspaces.find((w) => w.id === session.workspaceId) ?? null;
		return {
			user: session?.user ?? null,
			workspaces: session?.workspaces ?? [],
			workspace,
			isAuthenticated: !!session?.token,
			login: async (input) =>
				commit(toSession(await authApi.login(input), getSession()?.workspaceId)),
			signup: async (input) => commit(toSession(await authApi.signup(input))),
			logout,
			switchWorkspace: (workspaceId) => {
				const current = getSession();
				if (current?.workspaces.some((w) => w.id === workspaceId)) {
					commit({ ...current, workspaceId });
				}
			},
			adoptWorkspaces: (workspaces, workspaceId) => {
				const current = getSession();
				if (current) commit({ ...current, workspaces, workspaceId });
			},
			refresh,
		};
	}, [session, commit, logout, refresh]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
	return context;
}
